import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MUIDataTable from 'mui-datatables';
import { IconButton, Icon } from '@material-ui/core';
import round from 'helpers/mathRound';
import { TextFormattedNumber } from 'components';
import {
  deleteQuoteProductSubscriptionOptionApi,
  addTasksToQueueApi
} from 'api';
import { useStyles } from './styles';


const SubscriptionOptionsTable = ({ setEditableSubscription }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    editableQuoteProduct,
    quoteProductSubscriptionOptions
  } = useSelector((state: IReduxState) => state.quote);

  const { profile: { stores } } = useSelector((state: IReduxState) => state.user);
  const [{quoteProStoreCode}] = stores;

  const deleteSubscriptionOption = async (id) => {
    const isOneTimeEnable = editableQuoteProduct.custom_attributes?.find((a) => a.attribute_code === 'subscription_allow_onetime')?.value == 1;
    await deleteQuoteProductSubscriptionOptionApi({dispatch, id});
    const isDisableSubscriptionOption = (quoteProductSubscriptionOptions.length === 2 && isOneTimeEnable) ||
                                               (quoteProductSubscriptionOptions.length === 1 && !isOneTimeEnable);

    if (isDisableSubscriptionOption) {
      const editableAllowOneTimeAttr = editableQuoteProduct.custom_attributes?.find((el) => el.attribute_code === 'subscription_allow_onetime');

      const productToUpdate = {
        sku: editableQuoteProduct.sku,
        custom_attributes: [
          {attribute_code: 'subscription_allow_onetime', value: editableAllowOneTimeAttr?.value || '0'},
          {attribute_code: 'subscription_active', value: '0'}
        ]
      };

      const task = {
        body: productToUpdate,
        name: editableQuoteProduct.name,
        sku: editableQuoteProduct.sku,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode: quoteProStoreCode
      };

      addTasksToQueueApi({dispatch, tasks: [task]});
    }
  };

  const columns = [
    {
      name: 'frequency',
      label: 'Frequency',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {frequency_unit, frequency_count} = quoteProductSubscriptionOptions[dataIndex];

          return (
            <div className="flex items-center">
              {frequency_count != 0
                ? `Every${frequency_count == 1 ? '' : (` ${frequency_count}`)} ${frequency_unit}${frequency_count > 1 ? 's' : ''}`
                : 'One Time Purchase'
              }
            </div>
          );
        },
      }
    },
    {
      name: 'regular_price',
      label: 'Regular Price',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: () => (
          <div className="flex items-center">
            <TextFormattedNumber
              value={editableQuoteProduct?.price}
              prefix="$"
            />
          </div>
        ),
      }
    },
    {
      name: 'discount',
      label: 'Discount',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {frequency_count, additional_information} = quoteProductSubscriptionOptions[dataIndex];
          const discount_percentage = !additional_information?.length
            ? null
            : JSON.parse(`${additional_information[0]}`).discount_percentage;

          return (
            <div className="flex items-center">
              {discount_percentage && Number(discount_percentage)}{frequency_count != 0 ? '%' : ''}
            </div>
          );
        },
      }
    },
    {
      name: 'subscription_price',
      label: 'Subscription Price',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {additional_information} = quoteProductSubscriptionOptions[dataIndex];
          const discount_percentage = !additional_information?.length
            ? null
            : JSON.parse(`${additional_information[0]}`).discount_percentage;

          const price = discount_percentage ?
            (Number(editableQuoteProduct?.price) * (100 - Number(discount_percentage))) / 100
            :
            Number(editableQuoteProduct?.price);

          return (
            <div className="flex items-center">
              <TextFormattedNumber
                value={round(price)}
                prefix="$"
              />
            </div>
          );
        },
      }
    },
    {
      name: 'sproduct_label',
      label: 'Option Label',
      options: {
        filter: false,
        sort: false
      }
    },
    {
      name: 'ssort_order',
      options: {
        display: false,
        viewColumns: false
      }
    },
    {
      name: 'action',
      label: 'Actions',
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: () => ({style: {width: '150px'}}),
        customBodyRenderLite: (dataIndex) => {
          const {frequency_count, id} = quoteProductSubscriptionOptions[dataIndex];

          return (
            <>
              {frequency_count != 0 &&
                <div className="flex items-left">
                  <IconButton
                    className="mr-3 p-2"
                    title="Edit"
                    size="small"
                    onClick={() => setEditableSubscription(quoteProductSubscriptionOptions[dataIndex])}
                  >
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                  <IconButton
                    className="mr-3 p-2"
                    title="Delete"
                    size="small"
                    onClick={() => deleteSubscriptionOption(id)}
                  >
                    <Icon fontSize="small">delete</Icon>
                  </IconButton>
                </div>
              }
            </>
          );
        }
      }
    }
  ];

  return (
    <>
      {!!quoteProductSubscriptionOptions.length &&
        <MUIDataTable
          className={`m-3 ${classes.root}`}
          data={quoteProductSubscriptionOptions}
          columns={columns}
          options={{
            elevation: 0,
            filter: false,
            selectableRows: 'none',
            download: false,
            print: false,
            search: false,
            pagination: false,
            viewColumns: false,
            sortOrder: {
              name: 'ssort_order',
              direction: 'asc'
            }
          }}
        />
      }
    </>
  );
};

export default SubscriptionOptionsTable;
